import React, { useEffect, useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { decodeJwt } from "jose";
import DefaultSpinner from "../shared/DefaultSpinner";
import { ROOT } from "../../config/routes/paths";
import { useRegistrationSubscription } from "../../hooks/use-registration-subscription";
import { initiateAttendeeRegistration, initiateHubAttendeeRegistration } from "../../lib/api/public";
import { usePathPrefix } from '../../hooks/use-path-prefix';
import { normalizeRoutePath } from '../../lib/helpers/urlHelper';
import { authenticateWithSSOLogin } from "../../lib/auth";
import { CODE, DEFAULT_ERROR_MESSAGE, LOCALE, STATE, USER_STATE } from "../../lib/constants";
import { IAuthContext, useAuth } from "../../lib/context-providers/auth-context";
import { useMagicLinkAuth } from "../../hooks/use-magic-link-auth";

type JwtPayloadClient = 'event-hub' | 'external';

type JwtPayload = {
  registrationId?: string;
  client?: JwtPayloadClient;
};

type Props = {
  token?: string | null;
  code?: string | null;
  userState?: string | null;
};

export const LoadingPage: React.FC<Props> = ({ code, userState, token }: Props) => {
  const auth = useAuth() as IAuthContext;
  const { state, subscribe } = useRegistrationSubscription();
  const [showError, setShowError] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>('');
  const pathPrefix = usePathPrefix();
  const history = useHistory();
  const { authenticate, error: magicLinkError } = useMagicLinkAuth();

  const subscriptionInProgress = state.inProgress || !!(state.magicLink && !state.error);

  useEffect(() => {
    if (state.magicLink) {
      authenticate(state.magicLink);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.magicLink]);

  const handleSubscription = async (registrationId: string, client?: JwtPayloadClient) => {
    try {
      if (token) {
        await subscribe({ registrationId: registrationId, appSyncToken: token });
        client === 'event-hub'
          ? await initiateHubAttendeeRegistration({ registrationId: registrationId, appSyncToken: token, registrationApprovalEnabled: false })
          : await initiateAttendeeRegistration({ registrationId: registrationId, appSyncToken: token, registrationApprovalEnabled: false });
      }
    } catch (error) {
      setShowError(true);
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
    }
  };

  const resetUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete(CODE);
    params.delete(STATE);
    params.delete(LOCALE);
    params.delete(USER_STATE);

    history.replace({ search: params.toString() });
  };

  const handleSSOLogin = async (code: string, state: string) => {
    setIsLoading(true);
    try {
      if (!code || !state) {
        return;
      }
      auth.clearLocalSession();
      await authenticateWithSSOLogin(code, state);
      auth.setAsAuthenticated();
      resetUrlParams();
    } catch (error) {
      setShowError(true);
      const errorMsg = error?.message ?? DEFAULT_ERROR_MESSAGE;
      setErrorMessage(errorMsg);
      sessionStorage.setItem("SSO_ERROR", errorMsg);
      resetUrlParams();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;

    let registrationId: string | undefined;
    let client: JwtPayloadClient | undefined;

    try {
      const decoded: JwtPayload = decodeJwt(token) as JwtPayload;
      registrationId = decoded.registrationId;
      client = decoded.client;
    } catch (error) {
      setErrorMessage("Invalid token format.");
      return;
    }

    if (registrationId) {
      handleSubscription(registrationId, client);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!code || !userState) return;
    handleSSOLogin(code, userState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, userState]);

  useEffect(() => {
    if (magicLinkError) {
      setShowError(true);
      setErrorMessage(magicLinkError);
    }
  }, [magicLinkError]);

  useEffect(() => {
    if (!subscriptionInProgress && !showError) {
      setShowError(true);
    }
  }, [showError, subscriptionInProgress]);

  if (!token && !code && !userState) {
    return <Redirect to={normalizeRoutePath(ROOT, pathPrefix)} />;
  }

  const isLoading = subscriptionInProgress || loading;

  return (
    <div className="flex justify-center items-center h-64">
      {isLoading && (
        <DefaultSpinner visible={isLoading} />
      )}
      {(showError || errorMessage) && !isLoading && (
        <>
          <span className={`${errorMessage ? 'text-red-600' : ''}`}>{state.error?.message || errorMessage}</span>
          <hr className="mt-4 mb-4" />
        </>
      )}
    </div>
  );
};